/**
 * 영상·웹 페이지 썸네일 — 콘솔(브라우저)이 그 자리에서 만든다.
 *
 * 예전에는 영상은 영상 그림, 웹 페이지는 재생 그림 하나로 그렸다. 플레이리스트에 영상이 열 개면
 * 열 칸이 똑같아 **어떤 영상인지 제목을 읽어야만** 알았다.
 *
 * 서버가 장면을 떠 두면(파생본 `thumbnail`) 가장 깔끔하지만, 그러려면 서버에 영상 도구(ffmpeg)가
 * 있어야 하고 USB 설치 패키지·도커 이미지가 모두 커진다. 그래서 콘솔이 가진 것으로 만든다.
 *
 *   - **영상**은 모니터링 벽이 화면의 영상 장면을 뜨는 길(`stills.ts`)을 그대로 쓴다 — 한꺼번에
 *     몇 개씩, 같은 파일은 한 번만, 이 탭이 살아 있는 동안 기억한다. 화면 가까이 온 칸만 뜬다.
 *     저장소가 다른 출처라 그림으로 꺼낼 수 없으면 영상 요소를 그 장면에 세워 대신한다.
 *   - **웹 페이지**는 큰 칸에서 그 페이지를 줄여 그린다. 단 **스크립트는 돌리지 않는다**
 *     (`sandbox=""`) — 외부 페이지의 스크립트를 콘솔 안에서 돌리지 않는다는 원칙
 *     (`ContentDetail`·`ScreenFrame`)을 지킨다. 서버가 그려 보내는 페이지는 그대로 보이고,
 *     스크립트로 그리는 페이지는 비어 보인다 — 그래서 사이트 아이콘과 주소를 늘 함께 단다.
 *     작은 칸(편성 줄)에서는 사이트 아이콘만 — 페이지를 64px 로 줄이면 아무것도 읽히지 않는다.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Globe, Video } from 'lucide-react';
import type { TemplatePayload } from '@signageone/core';
import type { Asset } from '../api/client.js';
import { holdStill, videoFrame, videoLength, type Still } from './stills.js';
import { TemplatePreview } from './TemplatePreview.js';
import { useNearViewport } from './nearViewport.js';

// ─────────────────────────── 보이는 칸만 ───────────────────────────

// 따로 둔다 — HTML 미리보기(`HtmlContent.tsx`)도 쓰는데, 템플릿 미리보기가 그것을 겹쳐 그려 이 파일과 서로 부르게 된다.
export { useNearViewport } from './nearViewport.js';

// ─────────────────────────── 영상 — 한 장면 ───────────────────────────

/**
 * 장면을 뜰 시각(초).
 *
 * 첫 프레임은 페이드인으로 검거나 로고만 있는 경우가 많다. 앞 10% 지점(최대 3초)에서 뜬다 —
 * 그보다 뒤로 가면 읽어야 할 바이트가 늘고, 3초쯤이면 대개 본 장면이 시작돼 있다.
 * 길이를 모르면 0.1초 — 0초로 옮기면 브라우저에 따라 옮김(seeked)이 오지 않는다.
 */
export function frameTime(duration: number): number {
  if (!Number.isFinite(duration) || duration <= 0) return 0.1;
  return Math.max(0.1, Math.round(Math.min(3, duration * 0.1) * 100) / 100);
}

/** `0:30`, `1:05`, `1:02:03` — 영상 칸 구석의 길이 표시. */
export function durationLabel(seconds: number | null | undefined): string {
  if (!seconds || !Number.isFinite(seconds) || seconds <= 0) return '';
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const ss = String(total % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

/**
 * 영상 칸 — 뜬 장면, 그동안은 영상 그림.
 *
 * 구석에 영상 표시와 길이를 둔다 — 장면만 있으면 사진과 구분되지 않는다. 작은 칸(`compact`)은
 * 길이를 빼고 표시만 남긴다.
 */
export function VideoThumb({ asset, className, compact = false }: { asset: Asset; className: string; compact?: boolean }): ReactNode {
  const ref = useRef<HTMLDivElement>(null);
  const near = useNearViewport(ref);
  // 같은 파일이면 같은 장면 — 서명 주소는 몇 분마다 바뀌어 열쇠로 못 쓴다.
  const key = `asset:${asset.id}:${asset.checksum}`;
  const url = asset.status === 'ready' ? asset.preview_url : null;
  /*
   * 주소는 이 장면을 처음 뜰 때의 것을 붙든다. 목록을 다시 받으면 서명 주소가 바뀌는데,
   * 그걸 따라가면 대신 세운 영상 요소가 같은 파일을 다시 받는다.
   */
  const pinned = useRef({ key, url });
  if (pinned.current.key !== key) pinned.current = { key, url };

  const [held, setHeld] = useState<{ key: string; still: Still } | null>(null);
  const [length, setLength] = useState<number | null>(null);

  useEffect(() => {
    const source = pinned.current.url;
    if (!near || !source) return;
    let alive = true;
    /*
     * 이 칸이 떠 있는 동안 장면을 놓지 말라고 알린다. 칸은 한 번 받은 주소를 계속 들고 있어서,
     * 영상이 수백 개인 격자를 끝까지 내리는 사이 놓이면 돌아왔을 때 깨진 그림이 된다(`stills.ts`).
     */
    const release = holdStill(key);
    void videoFrame(key, source, frameTime).then((still) => {
      if (!alive) return;
      setHeld({ key, still });
      setLength(videoLength(key));
    });
    return () => {
      alive = false;
      release();
    };
  }, [near, key]);

  const still = held?.key === key ? held.still : undefined;
  const source = pinned.current.url;
  const seconds = asset.duration_sec || length;
  const label = compact ? '' : durationLabel(seconds);

  let body: ReactNode;
  if (typeof still === 'string') {
    body = <img src={still} alt="" className="h-full w-full object-cover" decoding="async" />;
  } else if (still === null && source) {
    // 그림으로 꺼내지 못했다(저장소가 다른 출처이고 CORS 를 허락하지 않는 등) — 그 장면에 멈춘 영상.
    body = (
      <video
        src={`${source}#t=${frameTime(seconds ?? Number.NaN)}`}
        muted
        playsInline
        preload="metadata"
        controls={false}
        className="pointer-events-none h-full w-full object-cover"
        aria-hidden="true"
        tabIndex={-1}
        onLoadedMetadata={(event) => {
          const duration = event.currentTarget.duration;
          if (Number.isFinite(duration) && duration > 0) setLength(duration);
        }}
      />
    );
  } else {
    body = (
      <div className="flex h-full w-full items-center justify-center">
        <Video className={compact ? 'h-4 w-4 text-slate-300' : 'h-7 w-7 text-slate-300'} strokeWidth={1.7} />
      </div>
    );
  }
  const shown = typeof still === 'string' || (still === null && Boolean(source));

  return (
    <div ref={ref} className={`${className} relative`}>
      {body}
      {shown ? (
        <span className={`media-badge ${compact ? 'is-compact' : ''}`} aria-hidden="true">
          <Video className="media-badge-icon" strokeWidth={2.2} />
          {label ? <span>{label}</span> : null}
        </span>
      ) : null}
    </div>
  );
}

// ─────────────────────────── 템플릿 ───────────────────────────

/**
 * 템플릿 칸 — **화면 가까이 온 칸만** 그린다.
 *
 * 템플릿 미리보기는 칸마다 iframe 하나에 렌더·글꼴 싣기까지 한다. 영상·웹 칸은 처음부터
 * 가까이 온 것만 떴는데 템플릿만 예외라, 템플릿이 수백 개인 계정에서는 목록을 여는 순간 iframe
 * 수백 개가 한꺼번에 섰다. 그 전까지는 같은 크기의 어두운 칸으로 자리만 지킨다 — 한 번 그린
 * 칸은 다시 걷지 않는다(`useNearViewport`).
 */
export function TemplateThumb({
  template,
  className,
  location,
}: {
  template: TemplatePayload;
  className: string;
  location: string;
}): ReactNode {
  const ref = useRef<HTMLDivElement>(null);
  const near = useNearViewport(ref);
  return (
    <div ref={ref} className={className}>
      {near ? <TemplatePreview template={template} location={location} /> : null}
    </div>
  );
}

// ─────────────────────────── 웹 페이지 ───────────────────────────

/** 웹 콘텐츠 주소의 출처와 호스트. http(s) 주소가 아니면 null. */
export function siteOf(url: string | null | undefined): { origin: string; host: string } | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
    return { origin: parsed.origin, host: parsed.host };
  } catch {
    return null;
  }
}

/** 사이트 아이콘. 못 받으면(없거나 막혔으면) 지구 그림. */
function SiteIcon({ origin, className }: { origin: string; className: string }): ReactNode {
  const [failed, setFailed] = useState(false);
  if (failed) return <Globe className={className} strokeWidth={1.7} />;
  return (
    <img
      src={`${origin}/favicon.ico`}
      alt=""
      className={`${className} object-contain`}
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
    />
  );
}

/** 웹 페이지를 그리는 가상 화면. 1920 이면 칸마다 큰 페이지를 그리느라 무겁고, 1280 이면 충분히 같아 보인다. */
const PAGE_WIDTH = 1280;
const PAGE_HEIGHT = 720;

/**
 * 웹 칸.
 *
 * `live` 면 그 페이지를 줄여 그린다. `sandbox=""` 라 스크립트·양식·창 열기·이동이 모두 막히고,
 * 페이지는 이름 없는 출처에서 돌아 자기 쿠키도 못 쓴다. 누르거나 초점을 받지 않는다.
 * 주소를 넘기지 않는다(`no-referrer`) — 그 사이트가 콘솔 주소를 알 필요가 없다.
 */
export function WebThumb({ asset, className, live = false }: { asset: Asset; className: string; live?: boolean }): ReactNode {
  const ref = useRef<HTMLDivElement>(null);
  const near = useNearViewport(ref);
  const [width, setWidth] = useState(0);
  const url = asset.source_url || asset.preview_url || '';
  const site = siteOf(url);
  const showPage = live && site !== null;

  useEffect(() => {
    const element = ref.current;
    if (!element || !showPage || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setWidth(entry.contentRect.width);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [showPage]);

  if (!showPage) {
    return (
      <div ref={ref} className={`${className} flex items-center justify-center bg-white`}>
        {site ? (
          <SiteIcon origin={site.origin} className="h-5 w-5 text-slate-400" />
        ) : (
          <Globe className="h-4 w-4 text-slate-300" strokeWidth={1.7} />
        )}
      </div>
    );
  }

  const scale = width > 0 ? width / PAGE_WIDTH : 0;
  return (
    <div ref={ref} className={`${className} relative overflow-hidden bg-white`}>
      {near && scale > 0 ? (
        <iframe
          src={url}
          title={`${asset.title} 미리보기`}
          sandbox=""
          loading="lazy"
          referrerPolicy="no-referrer"
          tabIndex={-1}
          aria-hidden="true"
          className="web-thumb-frame"
          style={{ width: PAGE_WIDTH, height: PAGE_HEIGHT, transform: `scale(${scale})` }}
        />
      ) : null}
      <span className="media-badge is-web" aria-hidden="true">
        <SiteIcon origin={site.origin} className="media-badge-icon" />
        <span className="truncate">{site.host}</span>
      </span>
    </div>
  );
}
